import { FC, useState } from 'react';
import {
  SupportMessage,
  SupportTicket,
  TicketType,
  useCreateSupportTicket,
  useSendSupportMessage,
  useSupportTicketDetail,
  useSupportTickets,
} from '../../hooks/useSupport';
import Typhography from '../../shared/Typhography/Typhography';
import { CustomSection } from '../../shared/CustomSection/CustomSecton';
import CustomButton from '../../shared/CustomButton/CustomButton';
import dayjs from 'dayjs';
import styles from './SupportoPage.module.scss';

const TYPE_LABELS: Record<TicketType, string> = {
  bug: 'Bug / Errore',
  question: 'Domanda',
  feedback: 'Feedback',
  other: 'Altro',
};

const STATUS_LABELS: Record<string, string> = {
  open: 'Aperto',
  in_progress: 'In lavorazione',
  resolved: 'Risolto',
  closed: 'Chiuso',
};

export const SupportoPage: FC = () => {
  const { data: ticketsData, isLoading } = useSupportTickets();
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);

  return (
    <div className="container-page">
      <div className="d-flex flex-column mb-3 gap-1">
        <Typhography type="h1" size="bold-24">
          Supporto e assistenza
        </Typhography>
        <Typhography size="regular-14" className={styles.muted}>
          Apri un ticket per segnalare bug, problemi o porre domande. Ti
          risponderemo il prima possibile.
        </Typhography>
      </div>

      {showNewForm && (
        <NewTicketForm
          onClose={() => setShowNewForm(false)}
          onCreated={(t) => {
            setShowNewForm(false);
            setSelectedTicketId(t._id);
          }}
        />
      )}

      <div className={styles.split}>
        <div className={styles.listColumn}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Typhography size="bold-16">I miei ticket</Typhography>
            <CustomButton
              size="sm"
              onClick={() => setShowNewForm(true)}
              disabled={showNewForm}
            >
              Nuovo ticket
            </CustomButton>
          </div>

          {isLoading ? (
            <Typhography size="regular-14" className={styles.muted}>
              Caricamento…
            </Typhography>
          ) : !ticketsData?.docs.length ? (
            <CustomSection className="p-3">
              <Typhography size="regular-14" className={styles.muted}>
                Nessun ticket aperto. Clicca "Nuovo ticket" per iniziare.
              </Typhography>
            </CustomSection>
          ) : (
            <div className="d-flex flex-column gap-2">
              {ticketsData.docs.map((t) => (
                <button
                  key={t._id}
                  className={`${styles.ticketCard} ${
                    selectedTicketId === t._id ? styles.ticketCardActive : ''
                  }`}
                  onClick={() => setSelectedTicketId(t._id)}
                >
                  <div className={styles.ticketHead}>
                    <span className={styles.ticketSubject}>{t.subject}</span>
                    <span className={styles[`status_${t.status}`]}>
                      {STATUS_LABELS[t.status]}
                    </span>
                  </div>
                  <div className={styles.ticketMeta}>
                    <span>{TYPE_LABELS[t.type]}</span>
                    <span>·</span>
                    <span>{dayjs(t.lastMessageAt).format('DD/MM HH:mm')}</span>
                    {t.unreadCountUser > 0 && (
                      <span className={styles.unreadDot}>
                        {t.unreadCountUser}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={styles.threadColumn}>
          {selectedTicketId ? (
            <TicketThread ticketId={selectedTicketId} />
          ) : (
            <CustomSection className="p-3">
              <Typhography size="regular-14" className={styles.muted}>
                Seleziona un ticket dalla lista per vedere la conversazione.
              </Typhography>
            </CustomSection>
          )}
        </div>
      </div>
    </div>
  );
};

const NewTicketForm: FC<{
  onClose: () => void;
  onCreated: (t: SupportTicket) => void;
}> = ({ onClose, onCreated }) => {
  const [type, setType] = useState<TicketType>('question');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const create = useCreateSupportTicket();

  const submit = async () => {
    if (!subject.trim() || body.trim().length < 5) return;
    const t = await create.mutateAsync({ type, subject: subject.trim(), body: body.trim() });
    onCreated(t);
  };

  return (
    <CustomSection className={`p-3 ${styles.newForm}`}>
      <Typhography size="bold-16" className="mb-2">
        Apri un nuovo ticket
      </Typhography>

      <div className={styles.formField}>
        <label htmlFor="ticket-type">
          <Typhography size="medium-12">Tipo</Typhography>
        </label>
        <select
          id="ticket-type"
          value={type}
          onChange={(e) => setType(e.target.value as TicketType)}
          className={styles.select}
        >
          {Object.entries(TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.formField}>
        <label htmlFor="ticket-subject">
          <Typhography size="medium-12">Oggetto</Typhography>
        </label>
        <input
          id="ticket-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={120}
          placeholder="Riassumi il problema"
          className={styles.input}
        />
      </div>

      <div className={styles.formField}>
        <label htmlFor="ticket-body">
          <Typhography size="medium-12">Descrizione</Typhography>
        </label>
        <textarea
          id="ticket-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={2500}
          placeholder="Spiega il problema in dettaglio…"
          rows={6}
          className={styles.textarea}
        />
      </div>

      <div className="d-flex gap-2 justify-content-end mt-2">
        <CustomButton color="outline" onClick={onClose}>
          Annulla
        </CustomButton>
        <CustomButton
          onClick={submit}
          isLoading={create.isPending}
          disabled={!subject.trim() || body.trim().length < 5}
        >
          Invia ticket
        </CustomButton>
      </div>
    </CustomSection>
  );
};

const TicketThread: FC<{ ticketId: string }> = ({ ticketId }) => {
  const { data, isLoading } = useSupportTicketDetail(ticketId);
  const sendMessage = useSendSupportMessage(ticketId);
  const [reply, setReply] = useState('');

  if (isLoading || !data) {
    return (
      <CustomSection className="p-3">
        <Typhography size="regular-14">Caricamento…</Typhography>
      </CustomSection>
    );
  }

  const { ticket, messages } = data;
  const isClosed = ticket.status === 'closed';

  const submit = async () => {
    if (!reply.trim()) return;
    await sendMessage.mutateAsync(reply.trim());
    setReply('');
  };

  return (
    <CustomSection className={`p-3 ${styles.threadBox}`}>
      <div className="d-flex flex-column gap-1 mb-3">
        <Typhography size="bold-16">{ticket.subject}</Typhography>
        <div className="d-flex gap-2 align-items-center">
          <span className={styles[`status_${ticket.status}`]}>
            {STATUS_LABELS[ticket.status]}
          </span>
          <Typhography size="regular-12" className={styles.muted}>
            {TYPE_LABELS[ticket.type]} ·{' '}
            {dayjs(ticket.createdAt).format('DD/MM/YYYY HH:mm')}
          </Typhography>
        </div>
      </div>

      <div className={styles.messages}>
        {messages.map((m) => (
          <MessageBubble key={m._id} message={m} />
        ))}
      </div>

      {isClosed ? (
        <Typhography
          size="regular-12"
          className={`${styles.muted} mt-3`}
        >
          Ticket chiuso, non puoi più rispondere.
        </Typhography>
      ) : (
        <div className={styles.replyBar}>
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            maxLength={5000}
            placeholder="Scrivi un messaggio…"
            rows={3}
            className={styles.textarea}
          />
          <CustomButton
            onClick={submit}
            disabled={!reply.trim() || sendMessage.isPending}
            isLoading={sendMessage.isPending}
          >
            Invia
          </CustomButton>
        </div>
      )}
    </CustomSection>
  );
};

const MessageBubble: FC<{ message: SupportMessage }> = ({ message }) => {
  const isStaff = message.authorType === 'staff';
  return (
    <div
      className={`${styles.messageRow} ${
        isStaff ? styles.messageRowStaff : styles.messageRowUser
      }`}
    >
      <div
        className={`${styles.bubble} ${
          isStaff ? styles.bubbleStaff : styles.bubbleUser
        }`}
      >
        <div className={styles.bubbleAuthor}>
          {isStaff ? 'Supporto Leopart' : message.authorName}
        </div>
        <div className={styles.bubbleBody}>{message.body}</div>
        <div className={styles.bubbleTime}>
          {dayjs(message.createdAt).format('DD/MM HH:mm')}
        </div>
      </div>
    </div>
  );
};

export default SupportoPage;
